import { useState } from 'react';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';

interface ArchiveHit {
  id: number;
  user_id: number;
  email: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  purged_at: string | null;
  reason_category: string | null;
  archive_expires_at: string | null;
}

interface ArchiveRecord {
  id: number;
  user_id: number;
  snapshot: Record<string, unknown>;
  snapshot_sha256: string | null;
  purged_at: string | null;
  archive_expires_at: string | null;
  swept_amount: string | null;
  reason_category: string | null;
  reason_text: string | null;
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function DeletionArchivePage() {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<ArchiveHit[] | null>(null);
  const [record, setRecord] = useState<ArchiveRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = async (event: React.FormEvent) => {
    event.preventDefault();
    if (query.trim().length < 3) {
      setError('Enter at least 3 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    setRecord(null);
    try {
      const res = await api.get<{ data: ArchiveHit[] }>(
        '/back-office/account-deletions/archive',
        { params: { query: query.trim(), per_page: 25 } }
      );
      setHits(res.data.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Archive search failed'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Opening a record is a separate call from searching, and it is logged. The
   * case reference is mandatory server-side, so there is no way to read a
   * deleted person's data without saying why.
   */
  const open = async (hit: ArchiveHit) => {
    const reason = window.prompt(
      'This read is logged against your account. Enter the ticket or case reference:'
    );
    if (reason === null) return;
    if (reason.trim().length < 3) {
      window.alert('A case reference is required to open an archived record.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<ArchiveRecord>(
        `/back-office/account-deletions/archive/${hit.id}`,
        { reason: reason.trim() }
      );
      setRecord(res.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Could not open the archived record'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">
          Deletion archive
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Records of accounts that have been purged, kept for audit and fraud
          investigation. Every record you open is logged against your account
          with the reference you give.
        </p>
      </div>

      <form onSubmit={search} className="flex gap-2">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Email, phone or name"
          className="w-full max-w-sm rounded border border-slate-300 px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={loading}
          className="rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {hits && hits.length === 0 && (
        <p className="text-sm text-slate-500">
          No archived accounts match that search.
        </p>
      )}

      {hits && hits.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Purged</th>
                <th className="px-3 py-2">Retained until</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {hits.map((hit) => (
                <tr key={hit.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-slate-900">
                    {`${hit.first_name ?? ''} ${hit.last_name ?? ''}`.trim() ||
                      `User ${hit.user_id}`}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {hit.email ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {hit.phone ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {formatDate(hit.purged_at)}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {formatDate(hit.archive_expires_at)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => open(hit)}
                      className="rounded border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                    >
                      Open (logged)
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {record && (
        <div className="space-y-3 rounded border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">
                Archived record #{record.id} · user {record.user_id}
              </h2>
              <p className="text-xs text-slate-500">
                Purged {formatDate(record.purged_at)} · retained until{' '}
                {formatDate(record.archive_expires_at)}
                {record.swept_amount
                  ? ` · ₦${record.swept_amount} forfeited`
                  : ''}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setRecord(null)}
              className="text-xs text-slate-500 hover:underline"
            >
              Close
            </button>
          </div>

          <div className="text-xs text-slate-500">
            Stated reason: {record.reason_category?.replace(/_/g, ' ') ?? '—'}
            {record.reason_text ? ` — “${record.reason_text}”` : ''}
          </div>

          {/* The digest is stored so a record can be proven unmodified years
              later; showing it makes that check possible without a DB query. */}
          {record.snapshot_sha256 && (
            <div className="break-all font-mono text-[10px] text-slate-400">
              sha256 {record.snapshot_sha256}
            </div>
          )}

          <pre className="max-h-96 overflow-auto rounded bg-slate-900 p-3 text-[11px] leading-relaxed text-slate-100">
            {JSON.stringify(record.snapshot, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
